// Percorso: src/pages/enrolShared.jsx
//
// I DUE PEZZI DI SCHERMO CHE I PERCORSI D'ISCRIZIONE HANNO IN COMUNE (RL-5a).
//
// Qui restano i due soli componenti: `LinkTermini`, la formula legale con
// l'elenco dei documenti del club, e `TornaAllaScelta`, il varco che riporta
// alla scelta del percorso. Stanno insieme perché hanno la stessa ragione di
// esistere: comparire IDENTICI in più pagine — nel percorso famiglia perfino
// più volte nella stessa pagina — e non divergere alla prima correzione.
//
// ⚠️ LE FUNZIONI PURE NON STANNO PIÙ QUI (cantiere «igiene di fine
// enrollment»). `GENERI`, `oggiAPerth`, `etaCompiuta`, `emailBenFormata`,
// `messaggioDelServer` e `messaggioDiErrore` sono migrate in
// `src/utils/enrolDati.js` tali e quali — stesse righe, stessi commenti — e
// si importano da lì. Stavano qui per storia, non per necessità: finché
// stavano accanto al JSX, chi ne voleva una si portava dietro React, il
// dizionario delle lingue e il foglio di stile dell'aiuto.
//
// ⚠️ UNA SOLA SEDE. `LinkTermini` è l'elenco dei documenti del club, nato
// identico in `Enrol.jsx` e `EnrolFamily.jsx`. Sta qui perché nel percorso
// famiglia va ripetuto una volta per il genitore E una volta per ogni minore:
// copiarlo N volte a mano è il modo sicuro per farlo divergere. Quando i
// documenti cambiano — è successo a settembre 2026, da quattro a cinque — si
// cambia questo file e cambiano tutte le schede insieme, `HomeIncomplete`
// compresa.
//
// ⚠️ LA VESTE, INVECE, È CAMBIATA (cantiere «grafica enrollment», passo 1).
// Le classi di `Register.css` — foglio ritirato — hanno lasciato il posto a
// quelle del tema: `.qpsc-en-check-text` per la formula d'accettazione e il
// pulsante fantasma §5.3 per il ritorno alla scelta. Nessun colore scritto a
// mano: i valori vivono in `src/theme.css` e le regole in
// `src/pages/Enrolment.css`.

import React from "react";
// Cantiere 2 (lingue d'aiuto): le parole del «torna alla scelta» vengono ora
// dal dizionario unico, e sotto l'inglese compare la riga d'aiuto quando una
// lingua è accesa. Il pulsante, la classe e la navigazione non cambiano.
import { testo } from "./enrolLingue";
import { Aiuto } from "./enrolAiuto";

// I DOCUMENTI DEL CLUB, IN UN SOLO ELENCO.
//
// ⚠️ SONO CINQUE, E I FILE SONO NUOVI (settembre 2026). Il club ha rifatto i
// PDF: nomi di file senza spazi né numerazione di revisione — che negli href
// vecchi finivano codificati a %20 doppi — e un documento in più, la Refund
// Policy. Il testo mostrato porta il prefisso QPSC, come la copertina dei
// documenti: chi apre la scheda ritrova a schermo il nome che leggerà sul PDF.
// I file stanno in `frontend/public/docs/` e li gestisce il committente: qui
// si scrivono soltanto i percorsi, che devono corrispondere alla lettera.
const DOCUMENTI = [
  { testo: "QPSC Membership Policy", href: "/docs/QPSC-Membership-Policy.pdf" },
  {
    testo: "QPSC Parents Code of Conduct",
    href: "/docs/QPSC-Parents-Code-of-Conduct.pdf",
  },
  {
    testo: "QPSC Parents Expectations",
    href: "/docs/QPSC-Parents-Expectations.pdf",
  },
  {
    testo: "QPSC Player Code of Conduct",
    href: "/docs/QPSC-Player-Code-of-Conduct.pdf",
  },
  { testo: "QPSC Refund Policy", href: "/docs/QPSC-Refund-Policy.pdf" },
];

// L'elenco dei documenti che si accettano, identico in tutti i moduli
// d'iscrizione (stesso testo, stessi file, stesse classi). Non è un componente
// «di stile»: è la formula legale, e deve esistere in un solo esemplare.
//
// ⚠️ IN COLONNA, NON IN FILA. Prima era una frase — «A, B, C and D» — con i
// nomi dei documenti oro dentro il testo corrente: a cinque voci quella riga
// diventa un paragrafo in cui i link si confondono fra loro e, su telefono,
// cadono a capo dove capita, spezzando i nomi. Ora è un elenco: «I accept the»
// e sotto un documento per riga, senza virgole e senza «and» — la formula
// resta una sola, ma si legge e si tocca. La colonna e l'area di tocco di ogni
// riga le fa `.qpsc-en-check-text a` (Enrolment.css): qui non c'è stile.
//
// ⚠️ RESTA IN INGLESE, anche a lingua d'aiuto accesa (cantiere 2): è la
// formula legale, e i documenti del club esistono solo in inglese. Nessun
// `<Aiuto>` qui — la riga tradotta prometterebbe un PDF che non c'è.
export function LinkTermini() {
  return (
    <span className="qpsc-en-check-text">
      I accept the
      {DOCUMENTI.map((doc) => (
        <a
          key={doc.href}
          href={doc.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {doc.testo}
        </a>
      ))}
    </span>
  );
}

// Il «torna alla scelta» dei due rami.
//
// È un'azione di SERVIZIO — porta via dal modulo, non lo manda avanti — quindi
// il pulsante FANTASMA del doc 07 §5.3: trasparente, bordo e testo oro, smusso
// 4px. Prima era un testo cliccabile con `#fbc02d` scritto a mano nello stile
// in linea, un oro che nella palette non esiste (§1.4).
// Lo stato spento non si dichiara qui: `.qpsc-ghost-btn:disabled` (AdminTools.css)
// porta già l'opacità 50% che il §5 prescrive, la stessa di ogni altro pulsante
// dell'app.
// `disabled` (RL-5b): serve a chiudere il varco mentre un invio è in volo, che
// altrimenti svuoterebbe il modulo a richiesta già partita. Vale di default
// `false`, quindi chi non lo passa si comporta esattamente come prima.
// Cantiere 2: la parola viene dal dizionario (stessa parola di prima) e la
// freccia resta sull'inglese — l'aiuto traduce parole, non segni. La riga
// d'aiuto sta FUORI dal pulsante, come nel mockup (`.btn + .help`): dentro
// avrebbe allargato l'area premibile a una riga di testo.
export function TornaAllaScelta({ onIndietro, disabled = false }) {
  return (
    <>
      <button
        type="button"
        className="qpsc-ghost-btn qpsc-en-ghost"
        onClick={onIndietro}
        disabled={disabled}
      >
        &larr; {testo("btn.chooseOther")}
      </button>
      <Aiuto k="btn.chooseOther" colore="oro" />
    </>
  );
}
